import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Suspense, lazy } from "react";
import NutMotif from "./NutMotif";

const StudAssembly3D = lazy(() => import("./StudAssembly3D"));

const HEADLINE = ["PRECISION STUD NUTS", "& STUD BOLTS MADE", "IN VADODARA, INDIA"];

const TRUST = ["ISO 9001:2015 CERTIFIED", "HIGH TENSILE & SS FASTENERS", "MAKARPURA GIDC · VADODARA"];

const HexBullet = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" className="shrink-0">
    <polygon points="5,0 9.3,2.5 9.3,7.5 5,10 0.7,7.5 0.7,2.5" fill="#1E56A0" />
  </svg>
);

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen bg-white bg-blueprint overflow-hidden flex flex-col justify-center pt-32 pb-20"
      data-testid="hero-section"
    >
      <NutMotif size={760} className="-left-64 -bottom-56" opacity={0.06} duration={110} />
      <NutMotif size={300} className="right-10 top-24" opacity={0.05} reverse duration={60} />
      <div className="relative z-10 max-w-7xl mx-auto w-full px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left — headline */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 font-sans font-medium text-[11px] sm:text-xs tracking-[0.12em] uppercase text-navy font-semibold mb-7"
            data-testid="hero-eyebrow"
          >
            <span className="w-10 h-[2px] bg-brand" />
            <span>ISO 9001:2015 CERTIFIED · EST. 2007 · VADODARA</span>
          </motion.div>

          <h1 className="font-display font-bold uppercase tracking-tight leading-[1.08] text-4xl sm:text-5xl">
            {HEADLINE.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1.5">
                <motion.span
                  className={`block ${i === 2 ? "text-brand" : "text-ink"}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.12 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`hero-line-${i + 1}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
            data-testid="hero-subline"
          >
            Established in 2007, Rahil Engineering Works is an ISO 9001:2015
            certified manufacturer of high tensile and stainless steel fasteners
            — ASTM A193 B7 stud bolts, ASTM A194 2H heavy hex nuts, hex bolts
            and custom machined components — from M12 to M100 and ¼" to 4", in
            any length, for refineries, power plants and EPC projects across
            India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#products"
              data-testid="hero-explore-catalogue-btn"
              className="group inline-flex items-center gap-3 bg-navy text-white font-sans font-medium text-xs uppercase tracking-[0.14em] font-bold px-8 py-4 rounded-md hover:bg-ink transition-colors duration-300"
            >
              Explore Products
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a
              href="#contact"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2 border-2 border-navy text-navy font-sans font-medium text-xs uppercase tracking-[0.14em] font-bold px-8 py-[14px] rounded-md hover:bg-navy hover:text-white transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="/catalogue.pdf"
              download="Rahil-Engineering-Works-Catalogue.pdf"
              data-testid="hero-download-catalogue-btn"
              className="group inline-flex items-center gap-2 bg-mist border border-steel-light text-navy font-sans font-medium text-xs uppercase tracking-[0.14em] font-bold px-6 py-[14px] rounded-md hover:border-brand hover:text-brand transition-colors duration-300"
            >
              Catalogue
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-300 group-hover:translate-y-0.5"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-3"
            data-testid="hero-trust-chips"
          >
            {TRUST.map((t) => (
              <span
                key={t}
                className="flex items-center gap-2 font-sans font-medium text-[11px] tracking-[0.14em] uppercase text-navy bg-mist border border-steel-light rounded-full px-4 py-2"
              >
                <HexBullet />
                {t}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Right — interactive 3D stud + twin nut assembly */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
            data-testid="hero-visual"
          >
            <Suspense
              fallback={
                <div className="aspect-square max-w-[520px] w-full mx-auto flex items-center justify-center">
                  <span className="font-sans font-semibold text-[11px] tracking-[0.14em] uppercase text-steel animate-pulse">
                    Loading assembly…
                  </span>
                </div>
              }
            >
              <StudAssembly3D />
            </Suspense>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: [0, -7, 0] }}
              transition={{
                opacity: { duration: 0.6, delay: 1.1 },
                y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1.7 },
              }}
              className="absolute top-40 left-0 sm:-left-6 bg-navy-deep text-white rounded-xl shadow-xl px-5 py-3.5 pointer-events-none"
              data-testid="hero-chip-size"
            >
              <div className="text-[11px] tracking-[0.12em] text-blue-300 font-semibold">SIZE RANGE</div>
              <div className="font-display font-bold text-lg leading-none mt-1.5">M12 — M100</div>
              <div className="font-sans font-semibold text-[11px] text-blue-200 mt-1.5">¼" — 4" (6 – 100 mm)</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: [0, -7, 0] }}
              transition={{
                opacity: { duration: 0.6, delay: 1.25 },
                y: { duration: 5.6, repeat: Infinity, ease: "easeInOut", delay: 2.1 },
              }}
              className="absolute top-6 right-0 sm:-right-4 bg-white border border-steel-light shadow-xl rounded-xl px-5 py-3.5 pointer-events-none"
              data-testid="hero-chip-thread"
            >
              <div className="text-[11px] tracking-[0.12em] text-steel font-semibold">THREAD FORMS</div>
              <div className="font-display font-bold text-lg leading-none mt-1.5 text-ink">UNC · UNF · METRIC</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2"
        data-testid="hero-scroll-hint"
      >
        <span className="font-sans font-medium text-[11px] tracking-[0.4em] uppercase text-steel">Scroll</span>
        <motion.span
          className="w-px h-10 bg-navy origin-top"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
